import apiClient from './base/apiClient'

/*
 * 대학교 화면들은 오류를 화면에 직접 보여준다(빨간 줄·다시 불러오기·토스트).
 * 공통 오류 토스트까지 뜨면 같은 문구가 두 번 나와서 끈다.
 */
const QUIET = { skipErrorHandler: true } as any

export interface University {
  id: number
  key: string
  name: string
  /** '@' 없이 온다. 화면에 보일 때만 앞에 붙인다. */
  domain: string
  logoPath: string | null
  /** 내 학교(/university/me)에만 온다. false 면 우리가 그 학교를 목록에서 내린 것 */
  isActive?: boolean
}

export interface UniversityMembership {
  email: string
  verifiedAt: string | null
  /** 소속 해제한 때. 값이 있으면 '다시 들어가기' 화면을 보여준다(메일 인증 없이 돌아온다). */
  leftAt: string | null
  university: University
}

export interface SendCodeResponse {
  success: boolean
  message: string
  expiresInSec: number
  resendAfterSec: number
}

export interface VerifyCodeResponse {
  verified: boolean
  message: string
  membership?: UniversityMembership
}

export const universityApi = {
  getUniversities: async (): Promise<University[]> => {
    const res = await apiClient.get<{ universities: University[] }>('/university/list', QUIET)
    return res.data.universities
  },

  getMembership: async (): Promise<UniversityMembership | null> => {
    const res = await apiClient.get<{ membership: UniversityMembership | null }>('/university/me', QUIET)
    return res.data.membership
  },

  sendCode: async (universityId: number, email: string): Promise<SendCodeResponse> => {
    const res = await apiClient.post<SendCodeResponse>(
      '/university/send-code',
      { universityId, email },
      QUIET
    )
    return res.data
  },

  verifyCode: async (code: string): Promise<VerifyCodeResponse> => {
    const res = await apiClient.post<VerifyCodeResponse>('/university/verify-code', { code }, QUIET)
    return res.data
  },

  /** 학교 소속 해제. 줄은 서버에 남아서 같은 학교로는 인증 없이 돌아온다. */
  leave: async (): Promise<void> => {
    await apiClient.post('/university/leave', {}, QUIET)
  },

  /** 해제했던 학교로 다시 참여 — 메일 인증 없이 */
  rejoin: async (): Promise<void> => {
    await apiClient.post('/university/rejoin', {}, QUIET)
  },
}

/** 학교 그룹 순위의 한 줄 */
export interface UniversityGroupMember {
  rank: number
  userId: string
  nickname: string | null
  profileImage: string | null
  /** 공개 기록 수 */
  recordCount: number
  isMe: boolean
}

export interface UniversityGroup {
  university: Pick<University, 'id' | 'key' | 'name' | 'logoPath'>
  memberCount: number
  members: UniversityGroupMember[]
}

/** 같은 학교 인증자들의 기록 수 순위. 인증 안 했으면 서버가 403 을 준다. */
export const getUniversityGroup = async (): Promise<UniversityGroup> => {
  const res = await apiClient.get<UniversityGroup>('/university/group', QUIET)
  return res.data
}
